/*
XOL-596 (slice 4, plan B9): the Edit / Save / Cancel of the four PRG progress steps
(ProgressStep1-4), which were four copies of one whole-row `saveForm`.

Each step is an EditPane form with an explicit Save (no auto-save, Carlos): Edit
re-GETs the step when its baseline has no version and holds every editable path
(R2.16); Save sends ONE versioned request per CHANGED content path (`status`,
`priority`, `remarks`, `state.<leaf>`; arrays whole), as one burst with one popup;
Cancel restores the baseline. The approval switch, reviewer and date (steps 1 and
3) are NOT content: if they changed, Save sends the PRG approval COMMAND
`{approved, approved_by, approved_at, expectedApproved}` (C10). XOL-596 (approval
lock): a Save that approves an unlocked step ASKS first (Save and lock / Save without
locking / Cancel); Save and lock sends the lock command once the approval went
through. A LOCKED step sends no approval command at all, and its three approval
controls are read-only. Nothing here ever
creates a step (`POST /step`) and `organisation_id` (structural) is never sent.

A component using this mixin provides, in data(): `step` (the step row, the ONE
object for that row), `dis` (the form is read-only), `prgPane` (its EditPane ref),
`prgHasApproval` (steps 1 and 3), and may define `prgBeforeSave()`.

ES5, no optional chaining or nullish coalescing.
------------------------------------------------------------------------ */

import * as sync from '@/lib/requestRowSync'
import { reportBurst } from '@/lib/syncNotices'
import * as savePopup from '@/lib/savePopup'
import { copyText } from '@/lib/requestSyncCopy'
import * as approvalLock from '@/lib/approvalLock'
import PermitMixins from '@/mixins/PermitMixins'
import RequestMixins from '@/mixins/RequestMixins'

// The content columns of a step the form edits. `state` is walked into leaves.
var CONTENT_COLUMNS = ['status', 'priority', 'remarks']

// Workflow-owned: only the approval command writes them.
var APPROVAL_KEYS = ['approved', 'approved_by', 'approved_at']

export default {
  mixins: [PermitMixins, RequestMixins],

  data () {
    return {
      // The Edit re-GET is running: Save is held shut.
      prgRefreshing: false,
      // A Save is running.
      prgSaving: false,
      // Lets Cancel outrun a re-GET.
      prgEditToken: 0
    }
  },

  beforeDestroy () {
    // Leaving while the form is still in edit mode discards the draft (the row goes
    // back to what the server said); a Save that is already running is left alone.
    var rec = sync.getRecord(this.step)

    if (rec) {
      if (rec.draft) {
        this.onCancel()
      }

      sync.releaseRow(this.step)
    }
  },

  computed: {
    // XOL-596 (approval lock): this step's approval is locked (an owned key).
    locked () {
      return this.approvalLocked(this.step)
    }
  },

  methods: {

    // The step's record, registered for the notices' row label ("{RTx} #{id} · Step {n}").
    prgRegister () {
      return this.syncRegisterRow(this.step, 'step', { form: 'progressStep' })
    },

    // Every path the form edits: the content columns and every leaf of `state`.
    prgPaths () {
      return CONTENT_COLUMNS.concat(sync.leafPaths(this.step, ['state']))
    },

    onEdit () {
      var self = this
      var step = this.step
      var token = ++this.prgEditToken

      this.prgRegister()

      var begin = function () {
        sync.holdPaths(step, self.prgPaths())
        self.dis = false
      }

      var rec = sync.getRecord(step)

      // The baseline has a version (rows from GET /actions/{id} carry one): hold.
      if (rec && typeof rec.lastAdoptedVersion === 'number') {
        begin()
        return
      }

      // No version to send: re-GET, adopt, and only then let the user edit.
      this.prgRefreshing = true

      this.refetchActionById(step.action_id || this.rowData.id, this.rowData).then(function (fresh) {
        if (token !== self.prgEditToken) {
          return
        }

        self.prgRefreshing = false

        var again = sync.getRecord(step)

        if (!fresh || !again || typeof again.lastAdoptedVersion !== 'number') {
          self.dis = true

          if (self.$refs[self.prgPane]) {
            self.$refs[self.prgPane].setEditMode(false)
          }

          savePopup.notify({
            type: 'error',
            title: 'Problem',
            message: copyText('requestSync.actionEdit.loadFailed'),
            duration: 5500
          })

          return
        }

        begin()
      })
    },

    // The approval command's body when the switch, reviewer or date changed since
    // the server last said, else null. Dates compare as dates (the picker emits
    // yyyy-MM-dd, the server a date-time).
    prgApprovalBody () {
      var step = this.step

      // A locked approval sends no command (the API would refuse it: 403
      // approval_locked); the controls are read-only anyway.
      if (this.approvalLocked(step)) {
        return null
      }

      var base = sync.getBaseline(step)
      var date = function (v) {
        return v ? this.formatDateSql(v) : null
      }.bind(this)
      var approved = !!step.approved
      var by = step.approved_by || null
      var at = date(step.approved_at)

      if (approved === !!base.approved && by === (base.approved_by || null) && at === date(base.approved_at)) {
        return null
      }

      // `expectedApproved` is what the server held, never the v-model, which has
      // already changed.
      return { approved: approved, approved_by: by, approved_at: at, expectedApproved: !!base.approved }
    },

    // POST /step/{id}/approval with the PRG body (C10). A stale decision (409) shows
    // the server's approval and says so; any other refusal puts the controls back.
    prgApprovalCommand (body) {
      var self = this
      var step = this.step

      return this.workflowCommand({
        kind: 'prgApproval',
        step: step,
        rows: [step],
        url: '/step/' + step.id + '/approval',
        body: body,
        label: 'Approval',
        hold: { row: step, keys: APPROVAL_KEYS },
        verify: function (fresh) {
          var row = self.stepOfAction(fresh, step.id)

          return !!row && !!row.approved === body.approved
        },
        revert: function () {
          self.revertWorkflowFields(step, APPROVAL_KEYS)
        }
      })
    },

    // Does this Save ask about the lock? It approves the step (the switch, Reviewer
    // or Date changed to approved), the step is not locked, and the user may lock it.
    prgAsksToLock () {
      if (!this.prgHasApproval || this.approvalLocked(this.step)) {
        return false
      }

      var rec = this.prgRegister()

      if (!this.rowHasId(rec)) {
        return false
      }

      var body = this.prgApprovalBody()

      return !!body && body.approved === true && this.canLockApproval(this.step)
    },

    // Save. A Save that approves an unlocked step asks first, BEFORE anything is sent:
    // Save and lock / Save without locking / Cancel (Cancel keeps the form in edit
    // mode with its draft; nothing is sent).
    onSave () {
      var self = this

      if (!this.prgAsksToLock()) {
        return this.prgSave(false)
      }

      return approvalLock.askLock(this.$root, {
        mode: 'prgSave',
        alive: function () { return !self._isDestroyed }
      }).then(function (answer) {
        if (answer === 'cancel') {
          self.prgStayInEdit()

          return { ok: false, status: 'cancelled' }
        }

        return self.prgSave(answer === 'lock')
      })
    },

    // EditPane.save() has already left edit mode: put it back (not onEdit, which
    // re-GETs, and not onCancel, which reverts the draft) and land on Save.
    prgStayInEdit () {
      var pane = this.$refs[this.prgPane]

      this.dis = false

      if (pane) {
        pane.setEditMode(true)

        this.$nextTick(function () {
          var button = pane.$el && typeof pane.$el.querySelector === 'function' ? pane.$el.querySelector('button.el-button--success') : null

          if (button && typeof button.focus === 'function') {
            button.focus()
          }
        })
      }
    },

    // The Save itself. `lock`: lock the approval once the approval command went
    // through (ok or recovered); never before.
    prgSave (lock) {
      var self = this
      var step = this.step

      if (typeof this.prgBeforeSave === 'function') {
        this.prgBeforeSave()
      }

      this.dis = true

      var rec = this.prgRegister()

      // A leaf that was not there on Edit (a key the user added) is a change too.
      sync.fieldsChangedUnder(step, 'state')
      sync.releasePaths(step, Object.keys(rec.entries))

      // The approval fields are not content: captured NOW, before the content
      // saves' responses (which carry the server's old `approved`) are adopted, and
      // held so those adopts leave the user's choice on screen until the command
      // decides.
      // A step row with no id is a bug: nothing is created (no `POST /step`) and no
      // command can address it. The content path below re-GETs the action instead.
      var body = this.prgHasApproval && this.rowHasId(rec) ? this.prgApprovalBody() : null

      if (body) {
        sync.holdOwned(step, APPROVAL_KEYS)
      }

      this.prgSaving = true

      // The Save's own hold protects the choice from the CONTENT saves' responses. Once
      // they are in it is let go (the command takes its own hold), so the command's
      // response decides the approval keys (adoptRow captures what is held before it
      // applies a command's response, XOL-596 decision fields).
      var released = false

      // `silent`: the content burst and the approval are reported as ONE popup below.
      return sync.flushRows([step], { reason: 'save', silent: true }).then(function (flushed) {
        if (!body) {
          return { flushed: flushed, approval: null, lockOut: null }
        }

        sync.releaseOwned(step, APPROVAL_KEYS)
        released = true

        return self.prgApprovalCommand(body).then(function (out) {
          // The lock only follows an approval that went through.
          if (lock && (out.status === 'ok' || out.status === 'recovered')) {
            return self.lockCommand(step, { quiet: true }).then(function (lockOut) {
              return { flushed: flushed, approval: out, lockOut: lockOut }
            })
          }

          return { flushed: flushed, approval: out, lockOut: null }
        })
      }).then(function (done) {
        self.prgFinish(done.flushed, done.approval, !!body && !released, done.lockOut)
      }, function (e) {
        console.error('XOL-596: saving the progress step threw', e)
        self.prgFinish(null, null, !!body && !released, null)
      })
    },

    // One popup for the whole Save: the saved paths (and "Approval" when the
    // command went through) or the merged "not saved" notice.
    prgFinish (flushed, approval, hadApproval, lockOut) {
      var step = this.step
      var results = flushed && flushed.results ? flushed.results.slice() : []

      if (hadApproval) {
        sync.releaseOwned(step, APPROVAL_KEYS)
      }

      this.prgSaving = false

      if (approval && approval.ok) {
        results.push({ status: 'saved', path: 'approved', kind: 'step', id: step.id, meta: sync.rowMeta(sync.getRecord(step)), value: true })
      }

      // "Approval locked" joins the Saved list when the lock went through.
      if (lockOut && lockOut.ok) {
        results.push({ status: 'saved', path: 'approvalLocked', kind: 'step', id: step.id, meta: sync.rowMeta(sync.getRecord(step)), value: true })
      }

      var summary = sync.summariseBurst(results)
      var burst = (flushed && flushed.burst) || { reason: 'save', scope: null, ordering: null }

      reportBurst(this.$root, burst, summary)

      // The approval was saved but the lock was not: say so (the line under Review &
      // Approval still offers Lock).
      if (lockOut && !lockOut.ok) {
        savePopup.notify({
          type: 'warning',
          title: copyText('requestSync.approvalLock.prgNotLocked.title'),
          message: copyText('requestSync.approvalLock.prgNotLocked.body'),
          duration: 5500
        })
      }
    },

    onCancel () {
      this.prgEditToken++
      this.prgRefreshing = false
      this.dis = true

      sync.revertRow(this.step, CONTENT_COLUMNS.concat(['state']))
      this.revertWorkflowFields(this.step, APPROVAL_KEYS)
    }

  }
}
